import { Box, ThemeProvider, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { theme } from '../../../theme/theme';
import {
	granularityPeriod,
	menuListOptions,
	opd,
	timePeriodOptions,
} from './constants';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '../../../theme/color-palette';
import ComparisonChart from './ComparisonChart';
import ColBarChart from './ColBarChart';
import MenuWithDefinition from '../../../components/MenuWithDefinition/MenuWithDefinition';
import { convertToInternationalCurrencySystem } from '../../../helper/convertAmount';
import { colorCoding, getIcon } from '../../../helper/getIcon';
import TimePeriodFilter from '../../../components/MenuWithDefinition/TimePeriodFilter';
import UpwardsDarkIcon from '../../../assets/SVGs/UpwardsDarkGreen.svg';
import DownwardsDarkIcon from '../../../assets/SVGs/DownwardsDarkRed.svg';
import ShimmerLineChart from '../../../components/ShimmerUI/ShimmerLineChart';
import DownloadComparison from '../../../components/Download/DownloadComparison';
import { saveEventTrack } from '../../../redux/actions/eventTrackAction';
import { UserPortalContext } from '../../../context/appcontext';
import {
	CATMANVIEW,
	comparisonError,
	MAIN_INSIGHTS_DASHBOARD,
	NETSALES,
	PERSONAVIEW,
} from '../../../constants/constant';
import { gql } from '@apollo/client';
import { getComparisonChartQuery } from '../../../apollo/queryBuilder/comparisonChartQueryBuilder';
import {
	clearMetricA,
	clearMetricB,
	setChartDataA,
	setChartDataB,
	setComparisonChartTimePeriod,
	setMetricA,
	setMetricALoadingStatus,
	setMetricB,
	setMetricBLoadingStatus,
	setOptionList,
	setOptionList2,
} from '../../../redux/actions/chartComparisonActions';
import client from '../../../apollo/mgql/index';
import { apolloHeaders } from '../../../apollo/utils';
import { debounce } from 'lodash';

const ComparisonChartWrapper = () => {
	const dispatch = useDispatch();
	const portalUserContext = useContext(UserPortalContext);

	const selectedOptionsPrimary = useSelector(
		(state) => state.chartComparisonReducer?.metricA
	);

	const selectedOptionsCompare = useSelector(
		(state) => state.chartComparisonReducer?.metricB
	);

	const metricALoading = useSelector(
		(state) => state.chartComparisonReducer?.isLoadingA
	);

	const metricBLoading = useSelector(
		(state) => state.chartComparisonReducer?.isLoadingB
	);

	const selectedFilters = useSelector(
		(state) => state.salesReducer.selectedFilters
	);

	const hierarchyFilterRhId = useSelector(
		(state) =>
			state?.fullCompanyViewReducer?.selectedHierarchy?.hierarchyFilterRhId
	);

	const { selectedUPC } = useSelector((state) => state.salesReducer);

	const {
		timePeriod,
		chartDataA,
		summaryValueA,
		summaryPctValueA,
		summaryValueB,
		summaryPctValueB,
		chartDataB,
		primaryOptionList: optionList,
		comparisonOptionList: optionList2,
	} = useSelector((state) => state.chartComparisonReducer);

	const handlePrimaryFilterChange = (option) => {
		dispatch(setMetricA({ metricA: option }));
	};

	const handleCompareFilterChange = (option) => {
		dispatch(setMetricB({ metricB: option }));
	};

	const setTimePeriod = (value) => {
		const payload = {
			timePeriod: value,
		};
		dispatch(setComparisonChartTimePeriod(payload));
	};

	const { isUPC, selectedView } = useSelector((state) => state.appGuideReducer);

	const hierarchyType =
		!selectedUPC.length > 0 ? 'rhIds' : isUPC ? 'wmUpcNbr' : 'wmItemNbr';

	const { selectedFilter, startDate, endDate } = selectedFilters;

	let previousSelectedFilter = useRef(selectedFilter);

	const sessionWeekFilters = JSON.parse(sessionStorage.getItem('weekfilters'));

	const { weekFilter } =
		sessionWeekFilters && Object.keys(sessionWeekFilters)?.length != 0
			? sessionWeekFilters
			: selectedFilters;

	const user =
		selectedView === CATMANVIEW ? PERSONAVIEW?.CATMAN : PERSONAVIEW?.SUPPLIER;

	// used to track the event for time filter selection
	useEffect(() => {
		const request = {
			event: 'Comparison Timeframe Selection',
			event_description: `Timeframe dropdown:: ${timePeriod} `,
			attributes: MAIN_INSIGHTS_DASHBOARD,
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	}, [timePeriod]);

	// used to track the event for primary seletected option
	useEffect(() => {
		const request = {
			event: 'Primary Metric Selection',
			event_description: `Primary Metric Drop Down: ${selectedOptionsPrimary}; Channel: ${selectedFilter?.toUpperCase()}`,
			attributes: MAIN_INSIGHTS_DASHBOARD,
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	}, [selectedOptionsPrimary]);

	// used to track the event for secondary seletected option
	useEffect(() => {
		const request = {
			event: 'Comparison Metric Selection',
			event_description: `Comparison Metric Drop Down: ${selectedOptionsCompare}; Channel: ${selectedFilter?.toUpperCase()}`,
			attributes: MAIN_INSIGHTS_DASHBOARD,
		};
		if (selectedOptionsCompare) {
			dispatch(saveEventTrack({ request, portalUserContext }));
		}
	}, [selectedOptionsCompare]);

	useEffect(() => {
		// re-setting the options once channel is changed
		if (previousSelectedFilter?.current !== selectedFilter) {
			dispatch(setMetricA({ metricA: NETSALES }));
			dispatch(setMetricB({ metricB: '' }));
			previousSelectedFilter.current = selectedFilter;
		}
		dispatch(
			setOptionList(menuListOptions[String(selectedFilter).toUpperCase()])
		);
		dispatch(
			setOptionList2(menuListOptions[String(selectedFilter).toUpperCase()])
		);
	}, [selectedFilter]);

	useEffect(() => {
		// filtering the option list based on selected channel and rejecting the option that matched the other selection
		const filteredOptionPrimary = menuListOptions[
			String(selectedFilter).toUpperCase()
		].filter((option) => option?.label !== selectedOptionsCompare);

		const filteredOptionSecondary = menuListOptions[
			String(selectedFilter).toUpperCase()
		].filter((option) => option?.label !== selectedOptionsPrimary);

		dispatch(setOptionList(filteredOptionPrimary));
		dispatch(setOptionList2(filteredOptionSecondary));
	}, [selectedOptionsPrimary, selectedOptionsCompare]);

	useEffect(() => {
		setTimePeriod(
			weekFilter === 0 || weekFilter === 1
				? 'Daily'
				: timePeriod !== 'Daily'
				? timePeriod
				: 'Weekly'
		);
	}, [weekFilter]);

	const isCompareMode = selectedOptionsCompare?.length !== 0;

	const primaryOptionObject = optionList.filter(
		(option) => option?.label === selectedOptionsPrimary
	);

	const compareOptionObject = optionList2.filter(
		(option) => option?.label === selectedOptionsCompare
	);

	const channel = selectedFilter?.toUpperCase();

	const primaryOptionName = `${primaryOptionObject[0]?.metricType}MetricValues`;
	const compareOptionName = `${compareOptionObject[0]?.metricType}MetricValues`;
	const primaryMetricPayload = gql`
		${getComparisonChartQuery(
			primaryOptionName,
			primaryOptionObject[0]?.metrics,
			hierarchyType,
			user
		)}
	`;

	const compareMetricPayload = gql`
		${getComparisonChartQuery(
			compareOptionName,
			compareOptionObject[0]?.metrics,
			hierarchyType,
			user
		)}
	`;

	const getMetricData = useCallback(
		debounce((payload) => {
			if (payload) {
				dispatch(setMetricALoadingStatus(true));
				client
					.query({
						query: primaryMetricPayload,
						variables: payload,
						fetchPolicy: 'cache-first',
						context: {
							headers: apolloHeaders(primaryOptionName),
						},
					})
					.then((res) => {
						dispatch(setChartDataA(res?.data));
					})
					.catch((err) => {
						console.log(err);
						dispatch(clearMetricA());
					});
			}
		}, 300)
	);

	const getCompareMetricData = useCallback(
		debounce((payload) => {
			if (payload) {
				dispatch(setMetricBLoadingStatus(true));
				client
					.query({
						query: compareMetricPayload,
						variables: payload,
						fetchPolicy: 'cache-first',
						context: {
							headers: apolloHeaders(compareOptionName),
						},
					})
					.then((res) => {
						dispatch(setChartDataB(res?.data));
					})
					.catch((err) => {
						console.log(err);
						dispatch(clearMetricB());
					});
			}
		}, 300)
	);

	useEffect(() => {
		if (
			selectedOptionsPrimary &&
			(selectedUPC?.length > 0 || hierarchyFilterRhId?.length > 0)
		) {
			getMetricData({
				startDate: startDate,
				endDate: endDate,
				granularity: granularityPeriod[timePeriod],
				channel: channel,
				rhIds: hierarchyFilterRhId?.split(','),
				wmUpcNbr: isUPC ? selectedUPC : '',
				wmItemNbr: isUPC ? '' : selectedUPC,
			});
		}
	}, [
		startDate,
		endDate,
		timePeriod,
		channel,
		hierarchyFilterRhId,
		selectedOptionsPrimary,
		selectedUPC,
		isUPC,
		user,
	]);

	useEffect(() => {
		if (
			selectedOptionsCompare != '' &&
			(selectedUPC?.length > 0 || hierarchyFilterRhId?.length > 0)
		) {
			getCompareMetricData({
				startDate: startDate,
				endDate: endDate,
				granularity: granularityPeriod[timePeriod],
				channel: channel,
				rhIds: hierarchyFilterRhId?.split(','),
				wmUpcNbr: isUPC ? selectedUPC : '',
				wmItemNbr: isUPC ? '' : selectedUPC,
			});
		}
	}, [
		startDate,
		endDate,
		timePeriod,
		channel,
		hierarchyFilterRhId,
		selectedOptionsCompare,
		selectedUPC,
		isUPC,
		user,
	]);

	const formatValue = (type, value) => {
		if (type === 'currency') {
			return '$' + convertToInternationalCurrencySystem(value);
		} else if (type === 'percent') {
			return convertToInternationalCurrencySystem(value) + '%';
		} else {
			return convertToInternationalCurrencySystem(value);
		}
	};

	const createChartValue = () => {
		const chartValue = [];
		const isCompareActive = compareOptionObject?.length !== 0;
		const numberFormat =
			primaryOptionObject[0]?.type === 'currency'
				? '$#.##a'
				: primaryOptionObject[0]?.type === 'percent'
				? '#.##p'
				: '#.##a';

		if (
			!(
				primaryOptionObject[0]?.label.includes('YoY % Change') ||
				opd?.includes(primaryOptionObject[0]?.id)
			)
		) {
			chartValue.push(
				{
					value: 'tyValue',
					name: isCompareActive
						? primaryOptionObject[0]?.label + ' this year'
						: 'This year',
					chartLine: '0',
					color: colors.PURPLE,
					secondaryColor: colors.PURPLE,
					numberFormat: numberFormat,
				},
				{
					value: 'lyValue',
					name: isCompareActive
						? primaryOptionObject[0]?.label + ' last year'
						: 'Last year',
					chartLine: '6,3',
					color: colors.PURPLE,
					secondaryColor: colors.LIGHT_PURPLE,
					numberFormat: numberFormat,
				}
			);
		} else {
			chartValue.push({
				value: 'tyValue',
				name: isCompareActive
					? primaryOptionObject[0]?.label + ' this year'
					: 'This year',
				chartLine: '0',
				color: colors.PURPLE,
				secondaryColor: colors.PURPLE,
				numberFormat: numberFormat,
			});
		}

		if (compareOptionObject?.length !== 0) {
			const numberFormat =
				compareOptionObject[0]?.type === 'currency'
					? '$#.##a'
					: compareOptionObject[0]?.type === 'percent'
					? '#.##p'
					: '#.##a';
			if (
				!(
					compareOptionObject[0]?.label.includes('YoY % Change') ||
					opd?.includes(compareOptionObject[0]?.id)
				)
			) {
				chartValue.push(
					{
						value: 'tyValueCompare',
						name: isCompareActive
							? compareOptionObject[0]?.label + ' this year'
							: 'This year',
						chartLine: '0',
						color: colors.ORANGE,
						secondaryColor: colors.ORANGE,
						isCompareActive: true,
						numberFormat: numberFormat,
					},
					{
						value: 'lyValueCompare',
						name: isCompareActive
							? compareOptionObject[0]?.label + ' last year'
							: 'Last year',
						chartLine: '6,3',
						color: colors.ORANGE,
						secondaryColor: colors.LIGHT_ORANGE,
						isCompareActive: true,
						numberFormat: numberFormat,
					}
				);
			} else {
				chartValue.push({
					value: 'tyValueCompare',
					name: isCompareActive
						? compareOptionObject[0]?.label + ' this year'
						: 'This year',
					chartLine: '0',
					color: colors.ORANGE,
					secondaryColor: colors.ORANGE,
					isCompareActive: true,
					numberFormat: numberFormat,
				});
			}
		}

		return chartValue;
	};

	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sept',
		'Oct',
		'Nov',
		'Dec',
	];

	const parseDataForChart = (primaryData = [], compareData = []) => {
		const isCompareDataEmpty = compareData?.length === 0;
		if (primaryData !== null) {
			return primaryData?.map((item, idx) => {
				return {
					...item,
					daily: item.daily,
					tyValueCompare: isCompareDataEmpty ? null : compareData[idx]?.tyValue,
					lyValueCompare: isCompareDataEmpty ? null : compareData[idx]?.lyValue,
					yoyCompare: isCompareDataEmpty ? null : compareData[idx]?.yoy,
					yoy_absCompare: isCompareDataEmpty
						? null
						: Math.abs(compareData[idx]?.yoy),
					yoy_abs: Math.abs(item.yoy),
					numberFormat:
						primaryOptionObject[0]?.type === 'currency'
							? '$#.##a'
							: primaryOptionObject[0]?.type === 'percent'
							? '#.##p'
							: '#a',
					numberFormatCompare: isCompareDataEmpty
						? null
						: compareOptionObject[0]?.type === 'currency'
						? '$#.##a'
						: compareOptionObject[0]?.type === 'percent'
						? '#.##p'
						: '#a',
					wmWkNbr: item.wmWkNbr,
					weekly: `Week ${item.wmWkNbr}`,
					monthly: `${String(
						months[Number(String(item.monthly).substring(0, 2)) - 1]
					)}'${String(item.monthly).substring(2, 4)}`,

					bpsChgRefArrow: item?.yoy > 0 ? UpwardsDarkIcon : DownwardsDarkIcon,
					bpsChgRefColor: item?.yoy > 0 ? '#113601' : '#590B0E',
					bpsChgRefBackgroundColor: item?.yoy > 0 ? '#EAF3E6' : '#F8D2D3',

					bpsChgRefArrowCompare: isCompareDataEmpty
						? null
						: compareData[idx]?.yoy > 0
						? UpwardsDarkIcon
						: DownwardsDarkIcon,
					bpsChgRefColorCompare: isCompareDataEmpty
						? null
						: compareData[idx]?.yoy > 0
						? '#113601'
						: '#590B0E',
					bpsChgRefBackgroundColorCompare: isCompareDataEmpty
						? null
						: compareData[idx]?.yoy > 0
						? '#EAF3E6'
						: '#F8D2D3',
				};
			});
		}
		return [];
	};

	const renderMetrics = (
		selectedObject,
		summaryValue = null,
		summaryPctValue = null
	) => {
		const newSummaryValue = selectedObject?.id?.includes('YoYPctChg')
			? summaryPctValue
			: summaryValue;

		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					padding: '0 8px',
				}}>
				{(summaryValueA === null || summaryValueA === undefined) &&
				(summaryPctValueA === null || summaryPctValueA === undefined) ? (
					<Typography
						sx={{
							fontSize: '18px',
							fontWeight: 700,
							lineHeight: '24px',
						}}>
						{'--'}
					</Typography>
				) : (
					<>
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: 700,
								lineHeight: '24px',
								color: selectedObject?.label.includes('YoY % Change')
									? colorCoding(newSummaryValue < 0 ? -1 : 1)
									: '',
							}}>
							{selectedObject?.label.includes('YoY % Change')
								? getIcon(newSummaryValue < 0 ? -1 : 1)
								: ''}
							{formatValue(selectedObject?.type, newSummaryValue)}
						</Typography>
						{!(
							selectedObject?.label.includes('YoY % Change') ||
							opd?.includes(selectedObject?.id)
						) && (
							<Typography
								sx={{
									fontSize: '14px',
									fontWeight: 700,
									lineHeight: '20px',
									display: 'flex',
									alignItems: 'center',
									color: colorCoding(summaryPctValue < 0 ? -1 : 1),
									padding: '0 4px',
								}}>
								{getIcon(summaryPctValue < 0 ? -1 : 1)}
								{Math.abs(summaryPctValue) + '%'}
							</Typography>
						)}
					</>
				)}
			</Box>
		);
	};

	/**
	 *
	 * @param {string} type 'No data' | 'No compare data'
	 * @returns React component
	 */
	const errorMessage = (type) => {
		const message =
			type === comparisonError.NO_DATA
				? comparisonError.NO_DATA_MESSAGE
				: comparisonError.NO_DATA_SUB_MESSAGE;
		const subMessage =
			type === comparisonError.NO_COMPARE_DATA
				? comparisonError.NO_COMPARE_DATA_MESSAGE
				: comparisonError.NO_COMPARE_DATA_SUB_MESSAGE;
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					height: '277px',
					justifyContent: 'center',
					top: '-72px',
					gap: '8px',
				}}>
				<Typography
					sx={{
						fontSize: '16px',
						fontWeight: 700,
						lineHeight: '24px',
						color: colors.DARK_GREY,
					}}>
					{message}
				</Typography>
				<Typography
					sx={{
						fontSize: '16px',
						fontWeight: 400,
						lineHeight: '24px',
						color: colors.LIGHT_GRAY_HEX,
					}}>
					{subMessage}
				</Typography>
			</Box>
		);
	};

	return (
		<ThemeProvider theme={theme}>
			<Box
				sx={{
					// width: '684px',
					height: '400px',
					border: ' 1px solid var(--Base-Minimum-Contrast, #E3E4E5)',
					borderRadius: '8px',
					padding: '16px',
				}}>
				{metricALoading || metricBLoading ? (
					<ShimmerLineChart />
				) : (
					<Box>
						<Box
							sx={{
								marginBottom: '24px',
							}}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}>
								<Box>
									<Typography
										sx={{
											fontSize: '18px',
											fontWeight: 400,
											lineHeight: '24PX',
											color: '#333',
										}}>
										{'Trend'}
									</Typography>
								</Box>
								<Box
									sx={{
										display: 'flex',
										gap: '8px',
										alignItems: 'center',
									}}>
									<TimePeriodFilter
										selectedOption={timePeriod}
										handleSelect={setTimePeriod}
										options={timePeriodOptions}
										weekFilter={weekFilter}
									/>
									<DownloadComparison
										metricA={chartDataA}
										selectedOptionsPrimary={selectedOptionsPrimary}
										metricB={chartDataB}
										selectedOptionsCompare={selectedOptionsCompare}
										chartTitle={'Comparison Chart'}
										seletedTimeFrame={timePeriod}
									/>
								</Box>
							</Box>
							<Box
								sx={{
									display: 'flex !important',
									gap: '16px',
								}}>
								<Box
									sx={{
										display: 'flex !important',
										flexDirection: 'column',
										padding: '8px 8px 8px 0',
									}}>
									<MenuWithDefinition
										id={'primary'}
										options={optionList}
										selectedOption={selectedOptionsPrimary}
										handleFilterChange={handlePrimaryFilterChange}
									/>
									{renderMetrics(
										primaryOptionObject[0],
										summaryValueA,
										summaryPctValueA
									)}
								</Box>

								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										padding: '8px 0 8px 8px',
									}}>
									<MenuWithDefinition
										id={'compare'}
										options={optionList2}
										selectedOption={selectedOptionsCompare}
										handleFilterChange={handleCompareFilterChange}
									/>
									{selectedOptionsCompare !== '' &&
										renderMetrics(
											compareOptionObject[0],
											summaryValueB,
											summaryPctValueB
										)}
								</Box>
							</Box>
						</Box>

						{/* supplier share chart */}

						{[...chartDataA, ...chartDataB]?.length === 0 ||
						(isCompareMode && chartDataB?.length === 0) ? (
							errorMessage(
								[...chartDataA, ...chartDataB]?.length === 0
									? comparisonError.NO_DATA
									: comparisonError.NO_COMPARE_DATA
							)
						) : chartDataA?.length <= 1 && chartDataB?.length <= 1 ? (
							<ColBarChart
								title={'BarChart'}
								chartData={parseDataForChart(chartDataA, chartDataB || [])}
								chartValue={createChartValue()}
								xAxisData={{
									Daily: 'daily',
									Weekly: 'weekly',
									Monthly: 'monthly',
								}}
								primaryOptionObject={primaryOptionObject[0]}
								compareOptionObject={
									isCompareMode ? compareOptionObject[0] : null
								}
								chartTitle={'Trend'}
								chartWidth={'100%'}
								chartHeight={'274px'}
								isCompareMode={isCompareMode}
								timePeriod={timePeriod}
							/>
						) : (
							<ComparisonChart
								chartName={'LineChart'}
								chartData={parseDataForChart(chartDataA, chartDataB || [])}
								chartValue={createChartValue()}
								xAxisData={{
									Daily: 'daily',
									Weekly: 'weekly',
									Monthly: 'monthly',
								}}
								primaryOptionObject={primaryOptionObject[0]}
								compareOptionObject={
									isCompareMode ? compareOptionObject[0] : null
								}
								chartTitle={'Trend'}
								chartWidth={'100%'}
								chartHeight={'274px'}
								isCompareMode={isCompareMode}
								timePeriod={timePeriod}
							/>
						)}
					</Box>
				)}
			</Box>
		</ThemeProvider>
	);
};

export default ComparisonChartWrapper;
